import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '@/services/api';
import { ShieldCheck, Search, Database, Clock, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/Button';

export default function AuditLogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: async () => {
      const res = await auditApi.getAll();
      return res.data.data;
    },
  });

  const filteredLogs = logs.filter((log: any) => {
    const searchString = `${log.actorEmail} ${log.entityId} ${log.action}`.toLowerCase();
    const matchesSearch = searchString.includes(searchTerm.toLowerCase());
    const matchesEntity = entityFilter ? log.entityType === entityFilter : true;
    const matchesAction = actionFilter ? log.action === actionFilter : true;
    return matchesSearch && matchesEntity && matchesAction;
  });

  const getActionColor = (action: string) => {
    if (action.includes('CREATE') || action.includes('APPROVE') || action.includes('LOGIN')) return 'text-green-700 bg-green-100';
    if (action.includes('DELETE') || action.includes('REJECT') || action.includes('CANCEL')) return 'text-red-700 bg-red-100';
    if (action.includes('UPDATE') || action.includes('ESCALATE')) return 'text-amber-700 bg-amber-100';
    return 'text-slate-700 bg-slate-100';
  };

  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Timestamp,Actor,Action,Entity Type,Entity ID,IP Address\\n";
    filteredLogs.forEach((row: any) => {
      csvContent += `${format(parseISO(row.createdAt),'yyyy-MM-dd HH:mm:ss')},${row.actorEmail},${row.action},${row.entityType},${row.entityId},${row.ipAddress}\\n`;
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `audit_log_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={handleExportCSV} variant="secondary" className="shadow-sm">
          <Download className="w-4 h-4 mr-2" /> Export to CSV
        </Button>
      </div>

      <div className="bg-slate-900 text-slate-300 rounded-2xl shadow-xl overflow-hidden font-mono text-sm border border-slate-800">
        
        {/* Filters */}
        <div className="p-4 border-b border-slate-800 bg-slate-950 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
            <input 
              type="text" 
              placeholder="Search by actor email, entity ID, or action..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-900 border border-slate-800 rounded outline-none text-slate-300 focus:border-blue-500 transition-colors"
            />
          </div>
          <div className="flex gap-4">
            <select 
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-slate-800 rounded outline-none text-slate-300 focus:border-blue-500 cursor-pointer"
            >
              <option value="">All Entities</option>
              <option value="USER">User</option>
              <option value="LEAVE">Leave</option>
              <option value="POLICY">Policy</option>
              <option value="HIERARCHY">Hierarchy</option>
            </select>
            <select 
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-slate-800 rounded outline-none text-slate-300 focus:border-blue-500 cursor-pointer"
            >
              <option value="">All Actions</option>
              <option value="USER_LOGIN">Logins</option>
              <option value="LEAVE_APPLY">Leave Applied</option>
              <option value="LEAVE_APPROVE">Leave Approved</option>
              <option value="LEAVE_REJECT">Leave Rejected</option>
            </select>
          </div>
        </div>

        {/* Log Viewer */}
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="bg-slate-900/50 text-xs uppercase text-slate-500 border-b border-slate-800 tracking-wider">
              <tr>
                <th className="px-6 py-3 font-medium">Timestamp</th>
                <th className="px-6 py-3 font-medium">Actor</th>
                <th className="px-6 py-3 font-medium">Action</th>
                <th className="px-6 py-3 font-medium">Target Entity</th>
                <th className="px-6 py-3 font-medium text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {isLoading ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-600"><Clock className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading logs...</td></tr>
              ) : filteredLogs.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-600"><Database className="w-5 h-5 mx-auto mb-2 opacity-50" /> No audit records found.</td></tr>
              ) : filteredLogs.map((log: any) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-slate-800/50 transition-colors">
                    <td className="px-6 py-3 text-slate-400">
                      {format(parseISO(log.createdAt), 'yyyy-MM-dd HH:mm:ss.SSS')}
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-blue-400">{log.actorEmail}</span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-purple-400 mr-2">{log.entityType}</span>
                      <span className="text-slate-500 text-xs">{log.entityId.substring(0,8)}...</span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button 
                        onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                        className="text-xs text-slate-500 hover:text-white transition-colors uppercase tracking-wider"
                      >
                        {expandedRow === log.id ? 'Hide' : 'Inspect'}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 bg-slate-950 border-t border-slate-800">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-slate-500 uppercase tracking-wider">Payload Data</span>
                          <span className="text-xs text-slate-500">IP: {log.ipAddress || 'Unknown'}</span>
                        </div>
                        <pre className="text-emerald-400 bg-[#0a0f18] p-4 rounded-lg overflow-x-auto text-xs border border-slate-800 shadow-inner">
                          {JSON.stringify(log.detailsJson, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
